import { useEffect, useState } from 'react';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import { Instagram, Youtube, Smartphone, Monitor, Video, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ContentFormData } from '..';

interface PlatformSelectProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export function PlatformSelect({
  formData,
  updateFormData,
  onValidationChange,
}: PlatformSelectProps) {
  const [orientation, setOrientation] = useState<'vertical' | 'landscape'>(
    formData.orientation || 'vertical'
  );
  const [platform, setPlatform] = useState<string | undefined>(
    formData.platform ||
      ((formData.orientation || 'vertical') === 'vertical' ? 'tiktok' : 'youtube')
  );

  // Sync the default platform into the form so Next is enabled immediately
  useEffect(() => {
    if (!formData.platform && platform) updateFormData({ orientation, platform });
    onValidationChange(!!platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrientationSelect = (value: 'vertical' | 'landscape') => {
    const defaultPlatform = value === 'vertical' ? 'tiktok' : 'youtube';
    setOrientation(value);
    setPlatform(defaultPlatform);
    updateFormData({ orientation: value, platform: defaultPlatform });
    onValidationChange(true);
  };

  const handlePlatformSelect = (value: string) => {
    setPlatform(value);
    updateFormData({ platform: value });
    onValidationChange(true);
  };

  const verticalPlatforms = [
    {
      id: 'tiktok',
      icon: <TikTokIcon className="h-8 w-8" />,
      name: 'TikTok'
    },
    {
      id: 'instagram',
      icon: <Instagram className="h-8 w-8" />,
      name: 'Reels'
    },
    {
      id: 'shorts',
      icon: <Youtube className="h-8 w-8" />,
      name: 'Shorts'
    }
  ];

  const landscapePlatforms = [
    {
      id: 'youtube',
      icon: <Youtube className="h-8 w-8" />,
      name: 'YouTube'
    },
    {
      id: 'video',
      icon: <Video className="h-8 w-8" />,
      name: 'Video'
    }
  ];

  const availablePlatforms = orientation === 'vertical' ? verticalPlatforms : landscapePlatforms;

      return (
    <div className="space-y-6 md:space-y-8">
      <div className="space-y-3 md:space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">Orientation</h3>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <button
            type="button"
            className={cn(
              "relative flex items-center gap-2 p-2.5 md:p-4 rounded-xl border-2 transition-all duration-200",
              orientation === 'vertical'
                ? "border-primary shadow-sm"
                : "border-border hover:border-primary/50"
            )}
            onClick={() => handleOrientationSelect('vertical')}
          >
            <Smartphone className={cn(
              "h-5 w-5 md:h-8 md:w-8 flex-shrink-0 transition-colors",
              orientation === 'vertical' ? "text-primary" : "text-muted-foreground"
            )} />
    <div>
              <div className="font-medium text-left text-sm md:text-base">Vertical</div>
              <div className="text-xs md:text-sm text-muted-foreground">9:16</div>
            </div>
          </button>

          <button
            type="button"
            className={cn(
              "relative flex items-center gap-2 p-2.5 md:p-4 rounded-xl border-2 transition-all duration-200",
              orientation === 'landscape'
                ? "border-primary shadow-sm"
                : "border-border hover:border-primary/50"
            )}
            onClick={() => handleOrientationSelect('landscape')}
          >
            <Monitor className={cn(
              "h-5 w-5 md:h-8 md:w-8 flex-shrink-0 transition-colors",
              orientation === 'landscape' ? "text-primary" : "text-muted-foreground"
            )} />
        <div>
              <div className="font-medium text-left text-sm md:text-base">Landscape</div>
              <div className="text-xs md:text-sm text-muted-foreground">16:9</div>
            </div>
          </button>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">Platform</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {availablePlatforms.map((platformOption) => (
            <button
              key={platformOption.id}
              type="button"
              className={cn(
                "relative flex items-center p-2.5 md:p-4 rounded-xl border-2 transition-all duration-200",
                platform === platformOption.id
                  ? "border-primary shadow-sm"
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => handlePlatformSelect(platformOption.id)}
            >
              <div className={cn(
                "flex-shrink-0 transition-colors",
                platform === platformOption.id ? "text-primary" : "text-muted-foreground"
              )}>
                {platformOption.icon}
              </div>
              <div className="ml-2.5 md:ml-4 flex-1">
                <div className="font-medium text-left text-sm md:text-base">{platformOption.name}</div>
              </div>
              {platform === platformOption.id && (
                <div className="flex-shrink-0 ml-1.5 md:ml-2">
                  <span className="px-1.5 md:px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    Selected
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-start gap-2 p-2.5 md:p-4 rounded-lg bg-muted/50">
          <div className="flex-shrink-0 mt-0.5">
            <Info className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            Coming Soon: Direct platform publishing. For now, you can download your content and manually upload it to your preferred platform.
          </p>
        </div>
          </div>
    </div>
  );
}